import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import type {
  AiCompanionMode,
  AiCompanionSidebar,
  AiCompanionSidebarContextType,
  SubjectArgs,
} from './types';
import { SIDEBAR_WIDTH_PIXELS, SIDEBAR_WIDTH_STORAGE_KEY } from './constants';
import { useUrlTracking, getCurrentPath } from './useUrlTracking';

const AiCompanionSidebarContext = createContext<
  AiCompanionSidebarContextType | undefined
>(undefined);

type AiCompanionSidebarProviderProps = {
  children: ReactNode;
};

export const AiCompanionSidebarProvider = ({
  children,
}: AiCompanionSidebarProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setModeState] = useState<AiCompanionMode>('sidebar');
  const [persistedWidth, persistSidebarWidth] = useLocalStorage<number>(
    SIDEBAR_WIDTH_STORAGE_KEY,
    SIDEBAR_WIDTH_PIXELS
  );
  // Separate in-memory state so drag frames don't write to localStorage on every tick.
  const [sidebarWidth, setSidebarWidth] = useState(persistedWidth);

  const [currentUrl, setCurrentUrl] = useState(getCurrentPath);

  const close = () => {
    setIsOpen(false);
    // Reset to sidebar mode when closing
    setModeState('sidebar');
  };

  const toggle = () => {
    setIsOpen(prev => !prev);
  };

  const setMode = (newMode: AiCompanionMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState(prev => (prev === 'sidebar' ? 'fullscreen' : 'sidebar'));
  };

  useUrlTracking(setCurrentUrl);

  // the native chat mounts below this provider, so the subject waits here until it reads it
  const [pendingSubject, setPendingSubject] = useState<SubjectArgs | null>(
    null
  );

  const setSubject = useCallback(
    (args: SubjectArgs) => {
      if (!isOpen) {
        setIsOpen(true);
      }
      setPendingSubject(args);
    },
    [isOpen]
  );

  const consumeSubject = useCallback(() => setPendingSubject(null), []);

  const value: AiCompanionSidebarContextType = {
    isOpen,
    mode,
    currentUrl,
    close,
    toggle,
    setMode,
    toggleMode,
    setSubject,
    sidebarWidth,
    setSidebarWidth,
    persistSidebarWidth,
    pendingSubject,
    consumeSubject,
  };

  return (
    <AiCompanionSidebarContext.Provider value={value}>
      {children}
    </AiCompanionSidebarContext.Provider>
  );
};

const useAiCompanionSidebarContext = (): AiCompanionSidebarContextType => {
  const context = useContext(AiCompanionSidebarContext);
  if (context === undefined) {
    throw new Error(
      'useAiCompanionSidebar must be used within AiCompanionSidebarProvider'
    );
  }
  return context;
};

// Internal hook - exposes full context for aiCompanion components
export const useAiCompanionSidebarInternal = useAiCompanionSidebarContext;

// Public hook - exposes only the public API for external consumers
export const useAiCompanionSidebar = (): AiCompanionSidebar => {
  const {
    isOpen,
    toggle,
    close,
    mode,
    setMode,
    toggleMode,
    setSubject,
    sidebarWidth,
  } = useAiCompanionSidebarContext();
  return {
    isOpen,
    toggle,
    close,
    mode,
    setMode,
    toggleMode,
    setSubject,
    sidebarWidth,
  };
};
